import React, { useCallback, useMemo, useState } from 'react';
import { IBaseDisbursementContainerProps } from '../IBaseDisbursementContainerProps';
import { ICheckDisbursementFormData } from 'types/order/escrow/checks/ICheckDisbursementFormData';
import { useField } from 'formik';
import { PageLoading } from 'ui-toolkit';
import { useSelectCurrentOrderId } from 'selectors/order/selectCurrentOrder';
import {
  usePrintAndSendDisbursementMutation,
  usePrintAndSetCheckDateMutation,
  usePrintCheckMutation,
} from 'API/order/escrow/CheckDisbursementAPI';
import { ModalTarget } from '../../lineItem/ModalTarget.Constant';
import { useGetEscrowBalancesDataQuery } from '../../../../../../API/order/escrow/EscrowBaseAPI';
import { assertDefined } from '../../../../../../utilities/Assertions';
import { checkForMutationSuccess } from 'API/checkForMutationSuccess';
import { ePermissions } from 'types/administration/userAdministration/ePermissions';
import { useHasPermissions } from '../../../../../accessControl/hooks/useHasPermissions';
import { CheckFormFields } from './CheckFormFields';
import { CheckDetailsDisplay } from './CheckDetailsDisplay';
import { CheckActions } from './CheckActions';
import { CheckModals } from './CheckModals';

export const CheckDisbursementContainer: React.FC<IBaseDisbursementContainerProps> = ({
  disbursementType,
  isDirty,
  TypeChange,
  isCardCollapsed,
}) => {
  const [{ value: data }] = useField<ICheckDisbursementFormData>('value');
  const orderId = useSelectCurrentOrderId();
  assertDefined(orderId);
  const [onPrintAndSendCheck, { isLoading: isPrintAndSendLoading }] = usePrintAndSendDisbursementMutation();
  const [onPrintCheck] = usePrintCheckMutation();
  const [printPreview, { isLoading: isPrintPreviewLoading }] = usePrintAndSetCheckDateMutation();

  const { data: escrowBalances } = useGetEscrowBalancesDataQuery(orderId);

  const [showPrintModal, setShowPrintModal] = useState(false);
  const [printedCheckData, setPrintedCheckData] = useState<Uint8Array | null>(null);

  const isReadOnly = useMemo(
    () => disbursementType.isReadOnly(data) || !disbursementType?.hasEditPermission,
    [disbursementType, data]
  );
  const isStaged = data && !data.isNew && !data.completedAt && !data.voidedAt && !data.sentAtDate;

  const hasDisbursePermissions = useHasPermissions([ePermissions.EditEscrowInitiateDisbursement]);

  const hasIssuanceDateEditPermissions = useHasPermissions([ePermissions.EscrowCheckIssuanceDateEdit]);

  const isCheckPrinted = useMemo(() => !!(data.printedDate ?? data.sentAtDate), [data.printedDate, data.sentAtDate]);

  const hasEnoughMoney = useMemo(
    () => Math.round((escrowBalances?.availableAmountDollars ?? 0) * 100) >= Math.round(data.amountCents ?? 0),
    [escrowBalances?.availableAmountDollars, data.amountCents]
  );
  const [showNotEnoughWarning, setShowNotEnoughWarning] = useState<boolean>(false);

  const onPrintAndSend = useCallback(async () => {
    setShowPrintModal(false);
    await onPrintAndSendCheck({ checkId: data.id, orderId });
  }, [setShowPrintModal, data, orderId, onPrintAndSendCheck]);

  const onPrint = useCallback(async () => {
    setShowPrintModal(false);
    await onPrintCheck({ checkId: data.id, orderId });
  }, [setShowPrintModal, data, orderId, onPrintCheck]);

  const createAndDisplayCheck = useCallback(async () => {
    const result = await printPreview({ checkId: data.id });
    if (checkForMutationSuccess(result, 'printing check preview') && 'data' in result) {
      setPrintedCheckData(result.data);
      setShowPrintModal(true);
    }
  }, [printPreview, data, setPrintedCheckData]);

  const onPrintClick = useCallback(async () => {
    if (hasEnoughMoney) {
      await createAndDisplayCheck();
    } else {
      setShowNotEnoughWarning(true);
    }
  }, [hasEnoughMoney, setShowNotEnoughWarning, createAndDisplayCheck]);

  const onIgnoreWarning = useCallback(async () => {
    setShowNotEnoughWarning(false);
    await createAndDisplayCheck();
  }, [setShowNotEnoughWarning, createAndDisplayCheck]);

  const targetElement = document.getElementById(ModalTarget);

  return (
    <PageLoading isLoading={isPrintPreviewLoading || isPrintAndSendLoading}>
      <CheckFormFields
        data={data}
        isReadOnly={isReadOnly}
        TypeChange={TypeChange}
        isCheckPrinted={isCheckPrinted}
        hasIssuanceDateEditPermissions={hasIssuanceDateEditPermissions}
        isCardCollapsed={isCardCollapsed ?? false}
        isDirty={isDirty}
      />
      <CheckDetailsDisplay data={data} />
      <CheckActions
        isStaged={isStaged}
        isReadOnly={isReadOnly}
        hasDisbursePermissions={hasDisbursePermissions}
        isDirty={isDirty}
        data={data}
        onPrintClick={onPrintClick}
        onPrintAndSend={onPrintAndSend}
      />
      <CheckModals
        showPrintModal={showPrintModal}
        printedCheckData={printedCheckData}
        targetElement={targetElement}
        setShowPrintModal={setShowPrintModal}
        onPrintAndSend={onPrintAndSend}
        showNotEnoughWarning={showNotEnoughWarning}
        onIgnoreWarning={onIgnoreWarning}
        setShowNotEnoughWarning={setShowNotEnoughWarning}
        onPrint={onPrint}
      />
    </PageLoading>
  );
};
